/**
 * User roles for role-based access control. Managed business-group tenants
 * issue `default-*` ids; legacy tenants carry the bare ids — nav gates
 * accept both.
 */
export type UserRole = 'employee' | 'default-employee' | 'planner' | 'default-planner' | 'admin';

/**
 * Navigation item configuration
 */
export interface NavItem {
  /** Unique identifier for the navigation item */
  id: string;

  /** Display label for the navigation item (i18n key) */
  label: string;

  /** Material Icon name (e.g., 'dashboard', 'calendar_month') */
  icon: string;

  /** Angular route path */
  route?: string;

  /** Allowed user roles (undefined = all roles can access) */
  roles?: UserRole[];

  /** Optional badge content (e.g., notification count) */
  badge?: string | number;

  /** Mark as coming soon (disables navigation and shows badge) */
  comingSoon?: boolean;
}

/**
 * Navigation section — a labeled group of nav items.
 * The label is display-only (not clickable).
 */
export interface NavSection {
  /** i18n key for the section label (e.g. 'navigation.sectionPlanning') */
  label: string;

  /** Items belonging to this section */
  items: NavItem[];

  /** Allowed user roles — if set, the entire section is hidden for users without these roles */
  roles?: UserRole[];
}

/**
 * Top-level navigation config entry — either a standalone nav item or a section.
 */
export type NavConfigEntry = NavItem | NavSection;

/**
 * Type guard: returns true if the entry is a NavSection (has `items` array).
 */
export function isNavSection(entry: NavConfigEntry): entry is NavSection {
  return 'items' in entry;
}
